/**
 * Cache configuration - TTL (Time-To-Live) per collection
 * 
 * Defines how long cached data should be kept before expiring.
 * TTL is in milliseconds. 0 = no caching.
 * 
 * Cache invalidation strategy:
 * - siteContent: Long TTL (24h) - Admin-managed, rarely changes
 * - products: Medium TTL (1h) - Stable product data
 * - guidebooks: Medium TTL (30m) - Guidebook metadata
 * - ideas: No cache (0) - User-generated, constantly changing
 */

/**
 * Cache Time-To-Live settings per collection
 * @type {Object<string, number>}
 */
export const CACHE_TTL = {
    // Admin-managed content, changes rarely - long TTL
    'siteContent': 24 * 60 * 60 * 1000,    // 24 hours
    
    // Product catalog - stable, occasional updates
    'products': 60 * 60 * 1000,            // 1 hour
    
    // Guidebook metadata - updates periodically
    'guidebooks': 30 * 60 * 1000,          // 30 minutes
    
    // Guidebook metadata subcollection
    '_metadata': 30 * 60 * 1000,           // 30 minutes
    
    // Default TTL for any collection not explicitly configured
    '_default': 5 * 60 * 1000              // 5 minutes
};

/**
 * Get cache TTL for a collection
 * 
 * Returns the configured TTL if available, otherwise returns the default TTL.
 * 
 * @param {string} collectionName - Name of the collection
 * @returns {number} TTL in milliseconds (0 = no cache)
 * 
 * @example
 * getCacheTTL('products') // 3600000 (1 hour)
 * getCacheTTL('ideas') // undefined, use default
 */
export function getCacheTTL(collectionName) {
    return CACHE_TTL[collectionName] !== undefined 
        ? CACHE_TTL[collectionName] 
        : CACHE_TTL._default;
}

/**
 * Check if a collection should be cached
 * 
 * A collection is cacheable if its TTL is greater than 0.
 * Useful for skipping cache operations entirely for non-cacheable collections.
 * 
 * @param {string} collectionName - Name of the collection
 * @returns {boolean} True if caching is enabled for this collection
 * 
 * @example
 * isCacheable('products') // true
 * isCacheable('ideas') // false (TTL not configured, uses default)
 */
export function isCacheable(collectionName) {
    return getCacheTTL(collectionName) > 0;
}
